import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { Container } from "@/components/common/Container";
import { Section } from "@/components/common/Section";
import { SectionHeading } from "@/components/common/SectionHeading";

import {
  Package,
  Boxes,
  Warehouse,
  GraduationCap,
  Contact,
  Users,
  Stethoscope,
  UtensilsCrossed,
  ArrowRight,
  UsersRound,
  LayoutDashboard
} from "lucide-react";

import {
  staggerContainer,
  fadeUp,
  viewportOnce,
} from "@/animations/motion";

const solutions = [
  {
    title: "Asset Management",
    // icon: PackageCheck,
    icon: Package,
    description:
      "Manage the complete lifecycle of physical assets with preventive maintenance, inspections, warranties, work orders and real-time asset tracking from a single platform.",
    link: "/solutions/asset-management",
  },
  {
    title: "ERP Solutions",
    icon: Boxes,
    description:
      "Integrate finance, procurement, inventory, operations and reporting into a unified ERP system that improves efficiency and provides complete business visibility.",
    link: "/solutions/erp",
  },
  {
    title: "CRM Solutions",
    // icon: Contact,
    icon: Users,
    description:
      "Strengthen customer relationships with lead management, sales pipelines, service requests, marketing automation and customer insights in one centralised platform.",
    link: "/solutions/crm",
  },
  {
    title: "Inventory Management",
    icon: Warehouse,
    description:
      "Monitor stock levels, warehouse operations, purchase orders and inventory movements in real time to reduce shortages and optimise inventory costs.",
    link: "/solutions/inventory-management",
  },
  {
    title: "School ERP",
    icon: GraduationCap,
    description:
      "Digitise admissions, academics, attendance, examinations, fees, transport and communication through an integrated education management platform.",
    link: "/solutions/school-erp",
  },
  {
    title: "Hospital Management",
    icon: Stethoscope,
    description:
      "Improve patient care and hospital operations by managing appointments, medical records, billing, pharmacy, laboratories and administrative workflows with AI powered automation.",
    link: "/solutions/hospital-management",
  },
  {
    title: "HRMS",
    icon: UsersRound,
    description:
      "Simplify employee onboarding, attendance, leave management, payroll, performance tracking and HR operations with a modern AI integrated human resource platform.",
    link: "/solutions/hrms",
  },
  {
    title: "Restaurant Management",
    icon: UtensilsCrossed,
    description:
      "Manage reservations, kitchen operations, POS integration, online ordering, inventory and customer loyalty from a single digital restaurant platform.",
    link: "/solutions/restaurant-management",
  },
  {
    title: "Customer Self-Service Portal",
    icon: LayoutDashboard,
    description:
      "Provide customers with secure self-service access to requests, documents, invoices, support tickets and real-time service updates.",
    link: "/solutions/customer-portal",
  },
];

export function Solutions() {
  return (
    <Section id="solutions">
      <Container>

        <SectionHeading
          eyebrow="Industry Solutions"
          title="Technology Solutions Tailored for Every Industry"
          description="Every industry has unique operational challenges. We design software solutions that address specific business needs while remaining scalable, secure and future-ready."
        />

        <motion.div
          variants={staggerContainer(0.08, 0.10)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-3"
        >
          {solutions.map((solution) => {
            const Icon = solution.icon;

            return (
              <motion.div
                key={solution.title}
                variants={fadeUp}
                className="group rounded-3xl border border-border bg-card p-8 transition-all duration-300 hover:-translate-y-2 hover:border-primary/25 hover:shadow-xl"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                  <Icon className="h-7 w-7" />
                </div>

                <h3 className="mt-6 font-heading text-2xl font-semibold">
                  {solution.title}
                </h3>

                <p className="mt-4 leading-7 text-muted-foreground">
                  {solution.description}
                </p>

                <Link
                  to={solution.link}
                  className="mt-8 inline-flex items-center gap-2 font-medium text-primary transition-all duration-300 hover:gap-3"
                >
                  Learn More

                  <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

      </Container>
    </Section>
  );
}